import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useRecordContext,
  useUpdate,
  useNotify,
  useDataProvider,
  useGetIdentity,
} from 'react-admin';
import { useQueryClient } from 'react-query';
import {
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import { MoreVertical } from 'lucide-react';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import {
  BenefitsStatusResult,
  BenefitsState,
  INELIGIBLE_REASON_PICKS,
} from './benefitsStatus';
import { CustomerEventType } from '../types';
import { BenefitsStatusBadge } from './BenefitsStatusBadge';
import DecFormValidation from './DecFormValidation';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

async function authHeaders(json = false): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${await getToken()}`,
    'Refresh-Token': await getRefreshToken(),
  };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

// A shared refresh signal so sibling components in the funeral sidebar (the
// headline benefits row and the CppBenefitsSection action rows) re-fetch the
// unified status after an action — send/discard/mark-ready/mark-ineligible all
// change data the status is derived from but which lives outside the funeral
// record, so react-admin's own refresh doesn't cover it.
// Actions the headline badge dispatches by state. Registered by the component
// that owns the modals (CppBenefitsSection) so the badge can be the single
// state-aware action button without those modals living in the headline.
export interface BenefitsActions {
  // Open the review/send flow for the next applicable form still to be sent.
  openNextUnsent?: () => void;
}

const BenefitsStatusRefreshContext = createContext<{
  version: number;
  bump: () => void;
  actions: BenefitsActions;
  setActions: (a: BenefitsActions) => void;
}>({ version: 0, bump: () => undefined, actions: {}, setActions: () => undefined });

export const BenefitsStatusRefreshProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [version, setVersion] = useState(0);
  const [actions, setActions] = useState<BenefitsActions>({});
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  const value = useMemo(
    () => ({ version, bump, actions, setActions }),
    [version, bump, actions],
  );
  return (
    <BenefitsStatusRefreshContext.Provider value={value}>
      {children}
    </BenefitsStatusRefreshContext.Provider>
  );
};

export function useBenefitsStatusRefresh() {
  return useContext(BenefitsStatusRefreshContext);
}

// --- Single-case hook (funeral sidebar, show header) -----------------------
export function useBenefitsStatus(
  funeralId?: number | string | null,
  refreshKey = 0,
) {
  const [status, setStatus] = useState<BenefitsStatusResult | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!funeralId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${PAPERMAN_API_URL}benefits/status/${funeralId}`,
        { headers: await authHeaders() },
      );
      if (res.ok) setStatus((await res.json()) as BenefitsStatusResult);
    } catch {
      /* best-effort — the row simply shows nothing on failure */
    } finally {
      setLoading(false);
    }
  }, [funeralId]);

  useEffect(() => {
    void reload();
    // refreshKey deliberately forces a re-fetch after sibling actions.
  }, [reload, refreshKey]);

  return { status, loading, reload };
}

// --- Batch provider (mobile list / any multi-record surface) ---------------
// value === null means "no provider in the tree" so a standalone field falls
// back to a single fetch. A provider always supplies an object (possibly empty
// while loading), so consumers can tell the two apart.
const BenefitsStatusContext = createContext<Record<
  number,
  BenefitsStatusResult
> | null>(null);

export const BenefitsStatusProvider: React.FC<{
  funeralIds: Array<number | string>;
  children: React.ReactNode;
}> = ({ funeralIds, children }) => {
  const [map, setMap] = useState<Record<number, BenefitsStatusResult>>({});

  // Stable key so we only refetch when the actual id set changes.
  const idsKey = useMemo(
    () =>
      Array.from(new Set((funeralIds || []).map((n) => Number(n))))
        .filter((n) => !isNaN(n))
        .sort((a, b) => a - b)
        .join(','),
    [funeralIds],
  );

  useEffect(() => {
    let cancelled = false;
    const ids = idsKey ? idsKey.split(',').map(Number) : [];
    if (ids.length === 0) {
      setMap({});
      return;
    }
    (async () => {
      const res = await fetch(`${PAPERMAN_API_URL}benefits/status/batch`, {
        method: 'POST',
        headers: await authHeaders(true),
        body: JSON.stringify({ funeralIds: ids }),
      });
      if (res.ok && !cancelled) {
        setMap((await res.json()) as Record<number, BenefitsStatusResult>);
      }
    })().catch(() => {
      /* best-effort */
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return (
    <BenefitsStatusContext.Provider value={map}>
      {children}
    </BenefitsStatusContext.Provider>
  );
};

// --- Record-aware field (works in or out of a provider) --------------------
// Renders the unified badge for the record in context. Inside a
// BenefitsStatusProvider it reads the shared batch map (one request per page);
// standalone it fetches the single case itself. Display-only — the actions
// (review / send / mail / mark ineligible) live on the funeral sidebar.
// Resolve the benefits status for the record in context — from the shared batch
// map when inside a BenefitsStatusProvider, else a standalone single fetch.
export function useRecordBenefitsStatus(): BenefitsStatusResult | undefined {
  const record = useRecordContext();
  const map = useContext(BenefitsStatusContext);
  const { version } = useBenefitsStatusRefresh();
  const inProvider = map !== null;
  const fromMap = record && map ? map[Number(record.id)] : undefined;
  const { status: single } = useBenefitsStatus(
    inProvider || !record ? null : record.id,
    version,
  );
  return fromMap ?? (inProvider ? undefined : single ?? undefined);
}

export const BenefitsStatusField: React.FC<{ size?: 'sm' | 'md' }> = ({
  size = 'md',
}) => {
  const status = useRecordBenefitsStatus();
  if (!status) return null;
  return (
    <BenefitsStatusBadge
      state={status.state}
      benefitsType={status.benefitsType}
      size={size}
      note={status.note}
    />
  );
};

// The state-aware action button for the funeral sidebar Benefits headline. The
// badge IS the button: click "Missing info" to see the field checklist, click
// "Ready to send"/"Sent" to review-and-send the next form still outstanding.
// The send/mail modals live in CppBenefitsSection, which registers its handlers
// via the actions bridge so this stays a single obvious action.
const BADGE_ACTIONABLE: BenefitsState[] = [
  'missing_information',
  'ready_to_send',
  'sent',
];

export const BenefitsHeadlineBadge: React.FC = () => {
  const record = useRecordContext();
  const status = useRecordBenefitsStatus();
  const { actions } = useBenefitsStatusRefresh();
  const [missingOpen, setMissingOpen] = useState(false);
  if (!status) return null;

  const state = status.state;
  const canOpenForm =
    (state === 'ready_to_send' || state === 'sent') && !!actions.openNextUnsent;
  const clickable = state === 'missing_information' || canOpenForm;

  const handleClick = () => {
    if (state === 'missing_information') setMissingOpen(true);
    else if (canOpenForm) actions.openNextUnsent?.();
  };

  const badge = (
    <BenefitsStatusBadge
      state={state}
      benefitsType={status.benefitsType}
      note={status.note}
    />
  );

  return (
    <>
      {clickable ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleClick(); }}
          title={
            state === 'missing_information'
              ? 'See missing information'
              : 'Review & send the next outstanding form'
          }
          className="inline-flex items-center rounded hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {badge}
        </button>
      ) : (
        badge
      )}

      <Dialog open={missingOpen} onClose={() => setMissingOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Benefits — missing information</DialogTitle>
        <DialogContent>
          {record?.id && <DecFormValidation funeralId={record.id} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissingOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Manual overrides for the sidebar Benefits headline. The ladder derives most
// states, but two need an agent: "Completed" (Quebec files QPP directly, so
// there's no DocuSeal send/mail flow to reach a terminal state — and it's an ON
// escape hatch) and "Ineligible" (family doesn't qualify). Both reuse the
// funerals.death_benefits_status column; 'ineligible' also suppresses
// field-chasing in the validation layer. Completed asks for a confirmation
// number (required), Ineligible for a reason (optional — never block staff);
// each lands on the case feed as a 'benefits_status_change' event. Bumps the
// shared refresh so the badge updates immediately.
//
// A hook, not a component, so the ⋮ menu and the note line under the headline
// open the same two prompts.
function useBenefitsOverride() {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const { bump } = useBenefitsStatusRefresh();
  const [completeOpen, setCompleteOpen] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [ineligibleOpen, setIneligibleOpen] = useState(false);
  const [reason, setReason] = useState('');

  const manual = (record?.death_benefits_status || '').toString().toLowerCase();
  const isIneligible = manual === 'ineligible';
  // The app can deploy before the migration adding benefits_ineligible_reason
  // runs. getOne selects *, so the key is only on the record once the column
  // exists — until then never write it (PostgREST rejects unknown columns).
  const hasReasonColumn = !!record && 'benefits_ineligible_reason' in record;
  const reasonPatch = (value: string | null) =>
    hasReasonColumn ? { benefits_ineligible_reason: value } : {};
  const isCompleted = manual === 'completed';

  // Best-effort, like the other feed events the admin writes: the status
  // change itself already succeeded.
  const logEvent = async (status: 'ineligible' | 'completed', note: string) => {
    if (!record) return;
    try {
      await dataProvider.create('customer_events', {
        data: {
          funeral_id: Number(record.id),
          timestamp: new Date().toISOString(),
          type: CustomerEventType.BENEFITS_STATUS_CHANGE,
          status,
          message: note || null,
          user_id: identity?.id ? Number(identity.id) : undefined,
          user_name: identity?.fullName || 'System',
        },
      });
      await queryClient.invalidateQueries(['funeral_feed']);
    } catch (e) {
      console.error('Failed to log benefits status event:', e);
    }
  };

  const apply = (
    data: Record<string, unknown>,
    successMsg: string,
    onSuccess?: () => void,
  ) => {
    if (!record) return;
    void update(
      'funerals',
      { id: record.id, data, previousData: record },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          notify(successMsg, { type: 'success' });
          bump();
          onSuccess?.();
        },
        onError: () => notify('Failed to update benefits status', { type: 'error' }),
      },
    );
  };

  const openCompleted = () => {
    setConfirmationNumber(record?.benefits_confirmation_number || '');
    setCompleteOpen(true);
  };
  const submitCompleted = () => {
    const value = confirmationNumber.trim();
    if (!value) return;
    const changed =
      !isCompleted || value !== (record?.benefits_confirmation_number || '');
    apply(
      { death_benefits_status: 'completed', benefits_confirmation_number: value },
      'Benefits marked completed',
      changed ? () => void logEvent('completed', value) : undefined,
    );
    setCompleteOpen(false);
  };

  const openIneligible = () => {
    setReason(record?.benefits_ineligible_reason || '');
    setIneligibleOpen(true);
  };
  const submitIneligible = () => {
    const value = reason.trim();
    const changed =
      !isIneligible || value !== (record?.benefits_ineligible_reason || '');
    apply(
      { death_benefits_status: 'ineligible', ...reasonPatch(value || null) },
      isIneligible ? 'Ineligible reason saved' : 'Benefits marked ineligible',
      changed ? () => void logEvent('ineligible', value) : undefined,
    );
    setIneligibleOpen(false);
  };

  const clear = () =>
    apply(
      {
        death_benefits_status: 'not_completed',
        benefits_confirmation_number: null,
        ...reasonPatch(null),
      },
      'Benefits status cleared',
    );

  const dialogs = (
    <>
      <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark benefits completed</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            required
            fullWidth
            margin="dense"
            size="small"
            label="Confirmation number"
            value={confirmationNumber}
            onChange={(e) => setConfirmationNumber(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitCompleted();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteOpen(false)}>Cancel</Button>
          <Button
            onClick={submitCompleted}
            variant="contained"
            disabled={!confirmationNumber.trim()}
          >
            Mark completed
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ineligibleOpen} onClose={() => setIneligibleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isIneligible ? 'Ineligible reason' : 'Mark benefits ineligible'}
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-wrap gap-1 mb-1">
            {INELIGIBLE_REASON_PICKS.map((pick) => (
              <Chip
                key={pick}
                label={pick}
                size="small"
                variant={reason.trim() === pick ? 'filled' : 'outlined'}
                onClick={() => setReason(pick)}
              />
            ))}
          </div>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            size="small"
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submitIneligible();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIneligibleOpen(false)}>Cancel</Button>
          <Button onClick={submitIneligible} variant="contained">
            {isIneligible ? 'Save reason' : 'Mark ineligible'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return {
    record,
    isLoading,
    isIneligible,
    isCompleted,
    openCompleted,
    openIneligible,
    clear,
    dialogs,
  };
}

export const BenefitsStatusOverrideMenu: React.FC = () => {
  const {
    record,
    isLoading,
    isIneligible,
    isCompleted,
    openCompleted,
    openIneligible,
    clear,
    dialogs,
  } = useBenefitsOverride();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  if (!record) return null;
  const hasOverride = isIneligible || isCompleted;

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
  };
  const closeMenu = () => setAnchor(null);
  const then = (fn: () => void) => () => {
    closeMenu();
    fn();
  };

  return (
    <>
      <button
        type="button"
        onClick={openMenu}
        disabled={isLoading}
        title="Benefits status override"
        className="inline-flex items-center rounded p-0.5 text-gray-400 hover:text-gray-600 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <MoreVertical className="w-4 h-4" aria-hidden={true} />
      </button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={closeMenu}>
        <MenuItem onClick={then(openCompleted)} disabled={isCompleted}>
          Mark completed
        </MenuItem>
        <MenuItem onClick={then(openIneligible)}>
          {isIneligible ? 'Edit ineligible reason' : 'Mark ineligible'}
        </MenuItem>
        {hasOverride && <Divider />}
        {hasOverride && <MenuItem onClick={then(clear)}>Clear status</MenuItem>}
      </Menu>
      {dialogs}
    </>
  );
};

// The agent's note, right under the sidebar Benefits headline so it reads
// without scrolling: "Not eligible — <reason>" / "Conf. #<number>". Truncated to
// one line (full text on hover); click to edit it in the same prompt.
export const BenefitsStatusNote: React.FC = () => {
  const { record, isIneligible, isCompleted, openCompleted, openIneligible, dialogs } =
    useBenefitsOverride();
  if (!record || !(isIneligible || isCompleted)) return null;

  const note = isIneligible
    ? (record.benefits_ineligible_reason || '').trim()
    : (record.benefits_confirmation_number || '').trim();
  const text = isIneligible
    ? note
      ? `Not eligible — ${note}`
      : 'Not eligible — add a reason'
    : note
      ? `Conf. #${note}`
      : 'Add confirmation #';

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (isIneligible) openIneligible();
          else openCompleted();
        }}
        title={note ? `${text} (click to edit)` : undefined}
        className={
          'block w-full truncate text-left text-[11px] leading-4 -mt-1 mb-1 hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded ' +
          (note ? 'text-muted-foreground' : 'text-muted-foreground/60 italic')
        }
      >
        {text}
      </button>
      {dialogs}
    </>
  );
};

export default BenefitsStatusField;
